import { useState } from 'react';
import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme
} from '@mui/material';
import { CheckCircle, Close, OpenInNew, WhatsApp, AutoAwesome } from '@mui/icons-material';
import { whatsNewMediaUrl } from 'api/app/whats-new-media.api';
import { IFeature, isAiFeature } from '../data/types';
import { colors, ctaGradient } from '../styles';
import { openFeatureContact } from '../contact';
import { trackFeatureInterest } from 'api/app/feature-interest.api';
import FeatureMedia from './FeatureMedia';
import PricingChip from './PricingChip';
import GoalPathChips from './GoalPathChips';
import FeaturePromoChips from './FeaturePromoChips';
import PromoHeader from './PromoHeader';

interface FeatureDialogProps {
    feature: IFeature | null;
    onClose: () => void;
}

const MEDIA_HEIGHT = { xs: 240, md: 420 };

/** מופרד לרכיב פנימי עם `key` לפי היכולת כדי שמצב הווידאו יתאפס בין יכולות */
const FeatureDialogBody = ({ feature, onClose }: { feature: IFeature; onClose: () => void }) => {
    const [videoFailed, setVideoFailed] = useState(false);
    const [interested, setInterested] = useState(false);
    const imageUrl = whatsNewMediaUrl(feature.imageFile);
    const videoUrl = whatsNewMediaUrl(feature.videoFile);
    const showVideo = Boolean(videoUrl) && !videoFailed;

    const markInterested = () => {
        if (!interested) {
            trackFeatureInterest(feature.id, true);
            setInterested(true);
        }
    };

    const openWhatsapp = () => {
        markInterested();
        openFeatureContact(feature.title);
    };

    return (
        <>
            <DialogTitle sx={{ p: 0 }}>
                <Box sx={{ position: 'relative', bgcolor: colors.softBg, filter: feature.comingSoon ? 'grayscale(1)' : undefined }}>
                    {showVideo ? (
                        <Box
                            component="video"
                            src={videoUrl}
                            poster={whatsNewMediaUrl(feature.posterFile) || imageUrl}
                            controls
                            playsInline
                            preload="none"
                            onError={() => setVideoFailed(true)}
                            sx={{
                                display: 'block',
                                width: '100%',
                                height: MEDIA_HEIGHT,
                                bgcolor: '#07182f',
                                objectFit: 'contain'
                            }}
                        />
                    ) : (
                        <FeatureMedia src={imageUrl} alt={feature.title} height={MEDIA_HEIGHT} />
                    )}
                    <IconButton
                        onClick={onClose}
                        sx={{
                            position: 'absolute',
                            top: 14,
                            insetInlineEnd: 14,
                            bgcolor: 'rgba(255,255,255,0.9)',
                            '&:hover': { bgcolor: '#ffffff' }
                        }}
                    >
                        <Close />
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ p: { xs: 2.5, md: 4 }, textAlign: 'start' }}>
                <Stack spacing={3.5} dir="rtl">
                    <Box>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                            {isAiFeature(feature) && (
                                <Chip
                                    icon={<AutoAwesome sx={{ fontSize: 16, color: '#ffffff !important' }} />}
                                    label="AI"
                                    sx={{
                                        fontWeight: 800,
                                        color: '#ffffff',
                                        background: feature.comingSoon
                                            ? 'linear-gradient(135deg, #9aa3ad, #7b8794)'
                                            : 'linear-gradient(135deg, #d4c4ff, #c4b5fd, #b39dff)'
                                    }}
                                />
                            )}
                            {feature.comingSoon && (
                                <Chip
                                    label="בקרוב"
                                    sx={{ fontWeight: 800, bgcolor: '#dc2626', color: '#ffffff' }}
                                />
                            )}
                            {feature.pilot && (
                                <Chip
                                    label="בפיילוט"
                                    sx={{ fontWeight: 800, bgcolor: '#e4d4ff', color: '#3b1d73' }}
                                />
                            )}
                            {feature.badge !== 'AI' && (
                                <Chip label={feature.badge} color="primary" sx={{ fontWeight: 800 }} />
                            )}
                            <PricingChip pricing={feature.pricing} size="medium" />
                            {feature.pricing.note && (
                                <Typography sx={{ color: colors.muted, fontSize: 14 }}>{feature.pricing.note}</Typography>
                            )}
                        </Stack>
                        {feature.promoHeader && (
                            <Box sx={{ mb: 1.5 }}>
                                <PromoHeader text={feature.promoHeader} />
                            </Box>
                        )}
                        <Typography variant="h2" dir="rtl" sx={{ fontWeight: 900, color: colors.ink, mb: 1.5, textAlign: 'start' }}>
                            {feature.title}
                        </Typography>
                        {feature.goalPath && (
                            <Box
                                sx={{
                                    mb: 2.5,
                                    p: 2,
                                    borderRadius: '18px',
                                    bgcolor: colors.tint,
                                    border: `1px solid ${colors.border}`
                                }}
                            >
                                <GoalPathChips path={feature.goalPath} size="large" />
                            </Box>
                        )}
                        {feature.promoChips && feature.promoChips.length > 0 && (
                            <Box sx={{ mb: 2.5 }}>
                                <FeaturePromoChips chips={feature.promoChips} size="large" />
                            </Box>
                        )}
                        <Typography sx={{ fontSize: 18, color: '#455a70', lineHeight: 1.8, whiteSpace: 'pre-line', textAlign: 'start' }}>
                            {feature.description}
                        </Typography>
                    </Box>

                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 900, color: colors.ink, mb: 1.5, textAlign: 'start' }}>
                            מה מקבלים
                        </Typography>
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                                gap: 2
                            }}
                        >
                            {feature.highlights.map((highlight) => (
                                <Stack
                                    key={highlight}
                                    direction="row"
                                    spacing={1.2}
                                    useFlexGap
                                    alignItems="center"
                                    sx={{
                                        p: 2,
                                        borderRadius: '18px',
                                        bgcolor: colors.tint,
                                        border: `1px solid ${colors.border}`,
                                        fontWeight: 700,
                                        color: '#17385f'
                                    }}
                                >
                                    <CheckCircle sx={{ color: '#0b7a45', fontSize: 20 }} />
                                    <Box>{highlight}</Box>
                                </Stack>
                            ))}
                        </Box>
                    </Box>

                    {feature.instructions && feature.instructions.length > 0 && (
                        <Box>
                            <Typography variant="h4" sx={{ fontWeight: 900, color: colors.ink, mb: 1.5, textAlign: 'start' }}>
                                איך מפעילים
                            </Typography>
                            <Stack spacing={1.5}>
                                {feature.instructions.map((step, index) => (
                                    <Stack key={step} direction="row" spacing={1.5} useFlexGap alignItems="flex-start">
                                        <Box
                                            sx={{
                                                flexShrink: 0,
                                                width: 30,
                                                height: 30,
                                                borderRadius: '50%',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontWeight: 900,
                                                color: '#ffffff',
                                                background: 'linear-gradient(135deg, #1264e2, #00a6c8)'
                                            }}
                                        >
                                            {index + 1}
                                        </Box>
                                        <Typography sx={{ color: colors.body, lineHeight: 1.8, pt: 0.3 }}>{step}</Typography>
                                    </Stack>
                                ))}
                            </Stack>
                        </Box>
                    )}
                </Stack>
            </DialogContent>

            <DialogActions
                sx={{
                    px: { xs: 2.5, md: 4 },
                    pb: 4,
                    pt: 0,
                    gap: 1.5,
                    flexWrap: 'wrap',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'flex-end'
                }}
            >
                <Stack spacing={1.5} sx={{ width: { xs: '100%', sm: 'auto' }, minWidth: 0, flex: 1 }}>
                    {interested && (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.25,
                                px: 2,
                                py: 1.4,
                                borderRadius: '18px',
                                bgcolor: '#e8f8ef',
                                border: '1px solid #b7e4c7'
                            }}
                        >
                            <CheckCircle sx={{ color: '#0b7a45', fontSize: 26, flexShrink: 0 }} />
                            <Box sx={{ minWidth: 0 }}>
                                <Typography sx={{ fontWeight: 900, color: '#0b7a45', lineHeight: 1.3 }}>
                                    מעולה, קיבלנו
                                </Typography>
                                <Typography sx={{ color: '#2d6a4f', fontSize: 14, lineHeight: 1.5 }}>
                                    ניצור איתך קשר בהקדם לגבי היכולת הזו
                                </Typography>
                            </Box>
                        </Box>
                    )}
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} useFlexGap alignItems={{ xs: 'stretch', sm: 'center' }}>
                        {feature.demoUrl && (
                            <Button
                                variant="outlined"
                                size="large"
                                startIcon={<OpenInNew />}
                                component="a"
                                href={feature.demoUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ borderRadius: '999px', px: 3, fontWeight: 800 }}
                            >
                                לצפייה בדוגמה
                            </Button>
                        )}
                        {!interested && (
                            <Button
                                variant="contained"
                                size="large"
                                onClick={markInterested}
                                sx={{ borderRadius: '999px', px: 4, fontWeight: 900, background: ctaGradient }}
                            >
                                אני רוצה לשמוע על זה
                            </Button>
                        )}
                        <Tooltip title="לשיחה בוואטסאפ">
                            <IconButton
                                onClick={openWhatsapp}
                                aria-label="לשיחה בוואטסאפ"
                                sx={{
                                    width: 48,
                                    height: 48,
                                    bgcolor: '#25D366',
                                    color: '#ffffff',
                                    alignSelf: { xs: 'flex-start', sm: 'center' },
                                    '&:hover': { bgcolor: '#1ebe5d' }
                                }}
                            >
                                <WhatsApp />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                </Stack>
                <Button onClick={onClose} color="inherit">
                    סגור
                </Button>
            </DialogActions>
        </>
    );
};

const FeatureDialog = ({ feature, onClose }: FeatureDialogProps) => {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

    return (
        <Dialog
            open={Boolean(feature)}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            fullScreen={fullScreen}
            dir="rtl"
            PaperProps={{
                dir: 'rtl',
                sx: {
                    borderRadius: fullScreen ? 0 : '28px',
                    overflow: 'hidden',
                    textAlign: 'start'
                }
            }}
        >
            {feature && <FeatureDialogBody key={feature.id} feature={feature} onClose={onClose} />}
        </Dialog>
    );
};

export default FeatureDialog;
